/* ============================================================
   ZNAKI · renderer.js
   Rysowanie sceny: papier, oś z podziałką, figury, ziarno,
   winieta. Canvas 2D, skalowanie DPR, wypiekane warstwy
   materii (mottle / ziarno / winieta) dla wydajności.

   Duża pomarańczowa tarcza może przyjść z pola `glitch` na
   specyfikacji (ustawia je figures.js) — wtedy zamiast koła
   wstawiamy jej rozdartą wersję policzoną przez shader
   (glitch-gl.js), a gdy WebGL zawiedzie, rysujemy ją pionowymi
   prostokątami (_drawGlitchDisc).
   ============================================================ */

import { PALETTE, PARAMS } from './config';
import { TAU, clamp, hash01, mulberry32 } from './utils';
import GlitchGL from './glitch-gl';

class Renderer {

  constructor(canvas) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d', { alpha: false });
    this.dpr = 1;
    this.w = 0; this.h = 0;        /* px CSS */
    this._grainTile = this._makeGrainTile();
    this._grainShift = { x: 0, y: 0, t: 0 };
    this._glitchGL = null;         /* shader tarczy — budzony przy pierwszym glitchu */
    this._glitchPath = '—';        /* którędy poszedł ostatni glitch (podgląd ?debug) */
    this.resize();
  }

  /* --------------------------------------------------------- */

  resize() {
    this.dpr = Math.min(2, window.devicePixelRatio || 1);
    /* canvas jest rozciągnięty na cały kontener instalacji (fixed, inset 0),
       więc mierzymy jego własne pudełko, a nie okno */
    this.w = this.canvas.clientWidth || window.innerWidth;
    this.h = this.canvas.clientHeight || window.innerHeight;
    this.canvas.width = Math.round(this.w * this.dpr);
    this.canvas.height = Math.round(this.h * this.dpr);
    this._bakeMottle();
    this._bakeVignette();
  }

  /* nierówność papieru: miękkie plamy walorowe */
  _bakeMottle() {
    const c = document.createElement('canvas');
    const n = 9;
    c.width = n; c.height = Math.max(6, Math.round(n * this.h / Math.max(1, this.w)));
    const x = c.getContext('2d');
    const rng = mulberry32(1234567);
    const img = x.createImageData(c.width, c.height);
    for (let i = 0; i < img.data.length; i += 4) {
      const v = 225 + Math.floor(rng() * 30);
      img.data[i] = v; img.data[i + 1] = v; img.data[i + 2] = v;
      img.data[i + 3] = 255;
    }
    x.putImageData(img, 0, 0);
    this._mottle = c;
  }

  _bakeVignette() {
    const c = document.createElement('canvas');
    c.width = Math.max(2, Math.round(this.w / 3));
    c.height = Math.max(2, Math.round(this.h / 3));
    const x = c.getContext('2d');
    const g = x.createRadialGradient(
      c.width / 2, c.height / 2, Math.min(c.width, c.height) * 0.42,
      c.width / 2, c.height / 2, Math.max(c.width, c.height) * 0.78
    );
    g.addColorStop(0, 'rgba(58, 44, 20, 0)');
    g.addColorStop(1, 'rgba(58, 44, 20, 0.30)');
    x.fillStyle = g;
    x.fillRect(0, 0, c.width, c.height);
    this._vignette = c;
  }

  /* kafelek ziarna: drobny szum o niskiej alfie */
  _makeGrainTile() {
    const s = 160;
    const c = document.createElement('canvas');
    c.width = s; c.height = s;
    const x = c.getContext('2d');
    const img = x.createImageData(s, s);
    const rng = mulberry32(987654);
    for (let i = 0; i < img.data.length; i += 4) {
      const v = 90 + Math.floor(rng() * 130);
      img.data[i] = v; img.data[i + 1] = v; img.data[i + 2] = v;
      img.data[i + 3] = rng() < 0.55 ? Math.floor(rng() * 34) : 0;
    }
    x.putImageData(img, 0, 0);
    return c;
  }

  /* --------------------------------------------------------- */

  render(specs, st) {
    const ctx = this.ctx;
    const PAL = PALETTE;
    const P = PARAMS;

    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);

    /* papier */
    ctx.globalCompositeOperation = 'source-over';
    ctx.globalAlpha = 1;
    ctx.fillStyle = PAL.bg;
    ctx.fillRect(0, 0, this.w, this.h);

    if (P.look.mottle > 0.01) {
      ctx.globalAlpha = 0.07 * P.look.mottle;
      ctx.globalCompositeOperation = 'multiply';
      ctx.imageSmoothingEnabled = true;
      ctx.drawImage(this._mottle, 0, 0, this.w, this.h);
      ctx.globalCompositeOperation = 'source-over';
      ctx.globalAlpha = 1;
    }

    /* delikatny przechył + oddech całej tablicy (reakcja na ruch) */
    const tilt = clamp(st.velSmooth, -1.6, 1.6) * P.motion.tilt * 0.0075;
    const boardScale = 1 - st.hold * 0.045;
    ctx.translate(st.cx, st.cy);
    ctx.rotate(tilt);
    ctx.scale(boardScale, boardScale);
    ctx.translate(-st.cx, -st.cy);

    this._drawAxis(st);

    /* figury wg głębi */
    specs.sort((a, b) => a.z - b.z);
    for (const s of specs) this._drawFig(s, st);

    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);

    /* materia na wierzchu */
    if (P.look.grain > 0.01) this._drawGrain(st);
    if (P.look.vignette > 0.01) {
      ctx.globalAlpha = P.look.vignette;
      ctx.globalCompositeOperation = 'multiply';
      ctx.drawImage(this._vignette, 0, 0, this.w, this.h);
      ctx.globalCompositeOperation = 'source-over';
      ctx.globalAlpha = 1;
    }
  }

  /* --- oś: pionowa linia + podziałka czytania ----------------- */

  _drawAxis(st) {
    const ctx = this.ctx;
    const P = PARAMS;
    const a = clamp(
      P.look.axisAlpha * (1 + st.velMag * 1.8 + st.hold * 2.2),
      0, 0.55
    );
    if (a <= 0.004) return;

    ctx.strokeStyle = PALETTE.ink;
    ctx.lineWidth = 1;

    ctx.globalAlpha = a;
    ctx.beginPath();
    ctx.moveTo(st.cx, 0);
    ctx.lineTo(st.cx, this.h);
    ctx.stroke();

    if (!P.look.ticks) return;

    /* podziałka związana ze światem — sunie wraz z tablicą */
    ctx.globalAlpha = a * 0.75;
    ctx.beginPath();
    const step = 0.2; /* vh */
    const first = Math.ceil((st.scroll - 0.05) / step) * step;
    for (let yv = first; yv < st.scroll + 1.1; yv += step) {
      const y = (yv - st.scroll) * st.vhPx;
      const major = Math.abs(yv - Math.round(yv)) < 0.001;
      const len = major ? 12 : 5;
      ctx.moveTo(st.cx - len, y);
      ctx.lineTo(st.cx + len, y);
    }
    ctx.stroke();
    ctx.globalAlpha = 1;
  }

  /* --- pojedyncza figura -------------------------------------- */

  _drawFig(spec, st) {
    const ctx = this.ctx;
    const f = spec.fig;
    const PAL = PALETTE;
    const vm = st.vminPx * spec.s;

    ctx.globalCompositeOperation = f.blend;
    ctx.globalAlpha = spec.alpha;
    ctx.fillStyle = PAL[f.color] || PAL.black;

    ctx.translate(spec.x, spec.y);
    if (spec.rot) ctx.rotate(spec.rot);

    if (f.shape === 'circle') {
      if (spec.glitch) {
        const onGpu = this._drawGlitchGL(spec, st, vm);
        if (!onGpu) this._drawGlitchDisc(spec, st, vm);
        this._glitchPath = onGpu ? 'shader' : 'prostokąty';
      } else {
        ctx.beginPath();
        ctx.arc(0, 0, Math.max(0.5, f.size.r * vm), 0, TAU);
        ctx.fill();
      }
    } else if (f.shape === 'bar' || f.shape === 'line') {
      const w = f.size.w * vm;
      const h = f.shape === 'line'
        ? Math.max(1.1, f.size.h * vm)     /* linie nie znikają przy małej skali */
        : f.size.h * vm;
      ctx.fillRect(-w / 2, -h / 2, w, h);
    } else if (f.shape === 'tri') {
      const w = f.size.w * vm, h = f.size.h * vm;
      ctx.beginPath();
      ctx.moveTo(w * 0.62, 0);
      ctx.lineTo(-w * 0.38, h / 2);
      ctx.lineTo(-w * 0.38, -h / 2);
      ctx.closePath();
      ctx.fill();
    }

    /* reset transformacji figury (wracamy do układu tablicy) */
    if (spec.rot) ctx.rotate(-spec.rot);
    ctx.translate(-spec.x, -spec.y);
    ctx.globalCompositeOperation = 'source-over';
    ctx.globalAlpha = 1;
  }

  /* --- rozdarta tarcza (glitch) -------------------------------- */

  /* Wersja z shadera: tarcza powstaje na ukrytym płótnie WebGL
     (glitch-gl.js) i wchodzi w scenę jednym drawImage — więc nadal
     podlega krycu, trybowi mieszania i sortowaniu po głębi.
     Kontekst budzimy dopiero przy pierwszym glitchu; jeśli WebGL
     nie działa lub kontekst przepadł, zwracamy false i rysunek
     przejmuje wersja na prostokątach.
     @param {Object} spec — specyfikacja rysowania (spec.glitch ≠ null)
     @param {Object} st — stan chwili
     @param {Number} vm — piksele na 1 vmin po skali figury
     @return {Boolean} czy shader narysował tarczę */
  _drawGlitchGL(spec, st, vm) {
    /* null = jeszcze nie próbowaliśmy, false = próba nieudana.
       Cokolwiek pójdzie nie tak po stronie GPU, MUSI skończyć się
       zwróceniem false, a nie wyjątkiem: render() leci w środku
       rysowania sceny, więc rzucony błąd zabiera ze sobą wszystkie
       figury narysowane po tej. */
    if (this._glitchGL === null) {
      try {
        this._glitchGL = new GlitchGL();
      } catch (e) {
        this._glitchGL = false;
        if (process.env.NODE_ENV === 'development') console.warn('[znaki] glitch: shader nie wstał', e);
      }
    }
    if (!this._glitchGL || !this._glitchGL.ok) return false;

    try {
      const r = Math.max(0.5, spec.fig.size.r * vm);
      const out = this._glitchGL.render(spec.glitch, PARAMS.glitch, r, this.dpr, st.t);
      if (!out) return false;
      this.ctx.drawImage(out.canvas, -out.w / 2, -out.h / 2, out.w, out.h);
      return true;
    } catch (e) {
      /* np. utrata kontekstu w locie — dalej rysują prostokąty */
      this._glitchGL.ok = false;
      if (process.env.NODE_ENV === 'development') console.warn('[znaki] glitch: shader padł w locie', e);
      return false;
    }
  }

  /* Koło rozsypuje się na pionowe pasy. Każdy pas to pionowa
     cięciwa koła na swojej szerokości, przesunięta w górę lub w dół
     o własną wartość; część pasów gaśnie (prześwit papieru), część
     gubi pomarańcz na rzecz czerni i bieli — stąd monochromia
     z materiału odniesienia.
     Losowanie skacze skokowo PARAMS.glitch.speed razy na sekundę,
     więc obraz "trzeszczy", zamiast płynnie falować.
     Rysujemy w układzie już przesuniętym na środek figury.
     @param {Object} spec — specyfikacja rysowania (spec.glitch ≠ null)
     @param {Object} st — stan chwili
     @param {Number} vm — piksele na 1 vmin po skali figury */
  _drawGlitchDisc(spec, st, vm) {
    const ctx = this.ctx;
    const G = PARAMS.glitch;
    const PAL = PALETTE;
    const r = Math.max(0.5, spec.fig.size.r * vm);

    /* liczbę pasów niesie samo zdarzenie — wylosowana raz, przy jego
       starcie (figures.js), więc w trakcie glitcha podział nie drga */
    const n = Math.max(4, Math.round(spec.glitch.slices));
    const bw = (r * 2) / n;
    const amp = r * G.strength;
    /* faza przetasowania — wspólna dla całego pasma w danej chwili */
    const base = spec.glitch.seed + Math.floor(st.t * Math.max(1, G.speed)) * 9301;

    for (let i = 0; i < n; i++) {
      const x0 = -r + i * bw;
      const xc = x0 + bw / 2;
      const hh = Math.sqrt(Math.max(0, r * r - xc * xc));   /* połowa cięciwy */
      if (hh < 0.5) continue;

      const k = base + i * 71;
      const h1 = hash01(k);
      const h2 = hash01(k + 3);
      const h3 = hash01(k + 11);

      if (h2 < 0.09) continue;                              /* pas zgaszony */

      /* większość pasów tylko drga — kilka wyrywa się w pionie */
      const dy = (h1 * 2 - 1) * amp * (h2 < 0.42 ? 1 : 0.16);
      /* pojedyncze pasy rozciągają się poza sylwetę: sygnał "wycieka" */
      const h = hh * (h3 > 0.93 ? 1 + (h3 - 0.93) * 5 : 1);

      ctx.fillStyle = h3 < G.mono
        ? (h1 < 0.5 ? PAL.black : PAL.white)
        : PAL.orange;
      ctx.fillRect(x0, dy - h, bw + 0.7, h * 2);            /* +0.7 — bez szczelin */
    }
  }

  /* czym rysował się ostatni glitch — 'shader', 'prostokąty' albo '—'
     (odczyt dla nakładki ?debug; bez WebGL scena po cichu wraca do
     prostokątów, więc bez tego nie widać, że shader nie wstał) */
  glitchBackend() {
    return this._glitchPath;
  }

  /* zwolnienie zasobów GPU — wołane z destroyPlayground() */
  destroy() {
    if (this._glitchGL) this._glitchGL.destroy();
    this._glitchGL = null;
  }

  /* --- ziarno filmowe ----------------------------------------- */

  _drawGrain(st) {
    const ctx = this.ctx;
    const g = this._grainShift;

    /* podmiana fazy ziarna ~11 razy na sekundę — "oddycha" jak film */
    if (st.now - g.t > 90) {
      g.t = st.now;
      g.x = Math.floor(Math.random() * 160);
      g.y = Math.floor(Math.random() * 160);
    }

    ctx.globalAlpha = 0.5 * PARAMS.look.grain;
    const s = 160;
    for (let y = -g.y; y < this.h; y += s) {
      for (let x = -g.x; x < this.w; x += s) {
        ctx.drawImage(this._grainTile, x, y);
      }
    }
    ctx.globalAlpha = 1;
  }
}

export default Renderer;
